import { type Pt, type Ring } from "./geometry"

// The editor's domain document and its wire mappings. Pure and dependency-free
// (vitest-covered): the store owns state transitions, this module owns shapes.
//
// Wire shapes mirror TakeoffSerialization / Takeoffs::Autosave on the Rails
// side — snake_case keys, rooms keyed by client-generated uuid.

export type PatternKind = "straight" | "brick" | "third" | "herringbone" | "stagger"

/**
 * What the room sits on — it drives adhesive, moisture barrier, prep, and
 * fastening method, so it prints on the material schedule as its own column.
 *
 * "unspecified" is a real, explicit third state, not a missing value: on a
 * room or sheet image `null` means "inherit the tier above", while an
 * explicit "unspecified" says "genuinely unknown" and prints a visible gap.
 * Better a gap than a confident wrong answer.
 */
export type Subfloor = "concrete" | "wood" | "unspecified"

/**
 * Snapshot of a catalog product assigned to a room, plus its layout. ONE
 * snake_case shape end-to-end (store, wire, rooms.product jsonb, savepoint
 * blobs) — it is stored verbatim, so a camelCase mirror would be pure drift
 * surface. Mirrors Room::PRODUCT_KEYS on the Rails side.
 *
 * size_w_in/size_l_in are the dims the pattern renders with — prefilled by
 * parsing format_label (the catalog has no numeric dimensions), editable on
 * the assignment. pattern is null for roll goods, which instead carry the
 * roll fields below (M7 seam planning).
 */
export type RoomProduct = {
  source: "fc_replica"
  id: string
  style: string | null
  color: string | null
  product_type: string | null
  format_label: string | null
  unit: string | null
  sqft_per_carton: number | null
  roll_width_ft: number | null
  vendor_name: string | null
  size_w_in: number | null
  size_l_in: number | null
  pattern: PatternKind | null
  layout_angle: number
  /** Room overlay hex ("#rrggbb"); null = palette default hashed from id. */
  overlay_color?: string | null
  /** Room tint opacity as a percentage; null = the palette default. */
  overlay_fill?: number | null
  /**
   * Roll goods only. usable_width_ft is the width all cut math runs against
   * ("nominal for print, usable for math" — 12' carpet is ~11'10" after edge
   * trim); null falls back to roll_width_ft minus the admin trim knob.
   * repeat_len_in + half_drop drive the pattern-repeat allowance every cut
   * after a roll's first pays. Optional so pre-M7 snapshots stay valid.
   */
  usable_width_ft?: number | null
  repeat_len_in?: number | null
  half_drop?: boolean
}

/**
 * The material-schedule spec a row carries: what goes on the surface and how
 * to install it. Shared by rooms and surfaces so both render identical
 * schedule rows — mirrors the MaterialSpec concern on the Rails side.
 */
export type MaterialSpec = {
  product: RoomProduct | null
  /** null = inherit (sheet image, then project). */
  subfloor: Subfloor | null
  /** Grout product or colour name; tile only. */
  grout: string | null
  /** Installer instructions as a LIST — joined with " ; " on render. */
  notes: string[]
}

/**
 * Which model drives a room's order quantity (refinements 2026-08-06):
 * `net` = net area × user margin — the rule-of-thumb number, no cut plan
 * required; `computed` = (net + computed waste from the cut plan) × margin —
 * the real number once seams exist. Sticky per room; null inherits the
 * project default (Measure's equivalent silently reverts — ours must not).
 */
export type WasteMode = "net" | "computed"

/**
 * One length seam: a strip boundary at a lay-space y (feet, anchor-relative).
 * `locked` pins it — a locked seam is a wall the drag-push cascade cannot
 * move, which is how a narrow fill strip is deliberately created.
 */
export type SeamOffset = { pos_ft: number; locked: boolean }

/**
 * A roll-goods room's seam layout, in LAY-SPACE: the room rotated by -angle
 * about its anchor (outline[0] — the same corner patterns pivot on) and
 * scaled to feet, so the roll's length runs along +x and strips stack along
 * +y. The angle shares layout_angle's semantics — one directionLabel serves
 * patterns and lay direction alike.
 *
 * `offsets` are the strip boundaries (each one is a length seam), ascending;
 * strips are the regions between the room's lay-space y-extent and those
 * boundaries — offsets.length + 1 strips. `cross` are T-seams: a cross cut
 * inside strip `strip` at lay-space x.
 *
 * `locked` is the per-ROOM seam lock (generator tools skip the room);
 * `auto` records whether the layout is still the proposed one (false after
 * any hand edit — the auto-lock escalation is driven by it and must be
 * visible, never silent).
 */
export type RoomSeams = {
  angle: number
  offsets: SeamOffset[]
  cross: { strip: number; pos_ft: number }[]
  fill_side: "start" | "end"
  locked: boolean
  auto: boolean
}

/**
 * A hand-placed cut: which roll, and where on it (lay: x along the length,
 * y across the width, feet). x_ft/y_ft null = "keep it on this roll but let
 * the allocator position it" — also what legacy M7 integer pins normalize
 * to. Pin = placed: the tray drag creates one, "Auto" releases it.
 */
export type CutPin = { roll: number; x_ft: number | null; y_ft: number | null }

/**
 * The human's cut-plan decisions, per product id across the whole takeoff
 * (cuts from every room nest onto shared rolls). Cuts themselves are DERIVED
 * from seam geometry — only pins persist. Cut keys:
 * "<room client_id>:<copy>:<strip>:<segment>". `locks` is a legacy M7 list
 * kept so old savepoint blobs restore; nothing writes it anymore.
 */
export type CutPlans = Record<string, { pins: Record<string, CutPin | number>; locks: string[] }>

/** Legacy M7 pins were bare roll indexes — normalize to the object form. */
export function normalizePin(pin: CutPin | number): CutPin {
  return typeof pin === "number" ? { roll: pin, x_ft: null, y_ft: null } : pin
}

export type EditorRoom = MaterialSpec & {
  id: string // client uuid — the server's rooms.client_id
  sheetId: number
  name: string
  outline: Ring
  holes: Ring[]
  waste: number
  /** Draw once, allocate N times — waste pools across the copies. */
  multiplier: number
  wasteMode: WasteMode | null
  seams: RoomSeams | null
}

/**
 * A schedule row with no geometry — shower walls, tub surround, backsplash.
 * Same spec field set as a room, never drawn, never measured.
 *
 * `parentRoomId` is a room's client uuid: the surface prints directly under
 * that room, which is how the document already reads. Ordering has exactly
 * one source — parent + position — so a drag rewrites the data rather than
 * setting a second "manual order" flag that could drift.
 */
export type EditorSurface = MaterialSpec & {
  id: string // client uuid — the server's surfaces.client_id
  name: string
  parentRoomId: string | null
  /**
   * Typed order quantity in the product's own unit (M8) — how wall tile
   * reaches the worksheet. Non-null IS the "manual, not measured" flag.
   */
  manualQty: number | null
}

/**
 * What an add-on IS, dual-source — snapshot of a catalog item (the same
 * picker-written shape as RoomProduct, minus layout) or a free-text one: RT's
 * Schluter profiles and drains may not exist in the catalog mirror, and
 * blocking on catalog completeness would push add-ons right back into the
 * Notes column M8 exists to drain. Stored verbatim (snake_case end-to-end);
 * mirrors AddOnSpec::CATALOG_ITEM_KEYS / MANUAL_ITEM_KEYS on the Rails side.
 */
export type AddOnItem =
  | {
      source: "fc_replica"
      id: string
      style: string | null
      color: string | null
      product_type: string | null
      format_label: string | null
      unit: string | null
      sqft_per_carton: number | null
      vendor_name: string | null
    }
  | { source: "manual"; name: string; unit: string }

/**
 * The deliberately small calculation-method set — what our geometry actually
 * supports, not the incumbent's 27. `count` and `manual` are typed figures;
 * the four measuring methods read the attached geometry and are the only
 * ones a coverage rate can ride.
 */
export type AddOnCalcMethod =
  | "room_area"
  | "room_perimeter"
  | "hole_area"
  | "hole_perimeter"
  | "count"
  | "manual"

/**
 * Gates an add-on to rooms whose RESOLVED subfloor matches (glue on
 * concrete, staple on wood). Never "unspecified": that is a cascade outcome,
 * not a trigger — an unspecified room matches no condition, so the estimator
 * sees a visible zero instead of a confidently wrong adhesive.
 */
export type SubfloorCondition = "concrete" | "wood"

/**
 * The calc fields shared by takeoff add-ons and product rules. coverage_rate
 * is the primitive the incumbent lacks: basis units covered by ONE item unit
 * (adhesive at 1 gal / 200 sf stores 200; the basis unit is implied by the
 * method — area methods measure SF, perimeter methods LF). min_qty is the
 * "min 2" of "1 EA per 550 SF, min 2"; it never resurrects a zero result.
 */
export type AddOnCalc = {
  calc_method: AddOnCalcMethod
  coverage_rate: number | null
  min_qty: number | null
  count_qty: number | null
  manual_qty: number | null
}

/**
 * A takeoff add-on attached at exactly one of three scopes (room / surface /
 * sheet — the teardown's variation-scope hierarchy minus the product tier,
 * which is a global AddOnRule). Nesting is prohibited by construction: an
 * add-on has no reference to another add-on.
 */
export type EditorAddOn = AddOnCalc & {
  id: string // client uuid — the server's add_ons.client_id
  parentRoomId: string | null
  parentSurfaceId: string | null
  sheetId: number | null
  item: AddOnItem
  subfloorCondition: SubfloorCondition | null
  /**
   * SHEET scope only: gate the computation to rooms carrying these product
   * types ("floor patch — LVP and Tile areas only"). Null/empty = any room.
   */
  productTypes: string[] | null
  /** Off keeps warehouse sundries off the installer document. */
  printOnSchedule: boolean
}

/**
 * A product-scope rule: attach pad to a carpet ONCE and it binds — at
 * compute time, by catalog product id — to every room and surface using that
 * product, in every takeoff. Server-owned rows (fetch-JSON CRUD by DB id,
 * never autosaved, not undoable); `manual` is disallowed here — a flat typed
 * total has no per-room meaning when a rule fans out.
 */
export type AddOnRule = AddOnCalc & {
  id: number
  productId: string
  position: number
  item: AddOnItem
  subfloorCondition: SubfloorCondition | null
  printOnSchedule: boolean
}

export type Calibration = { ftPerPx: number | null; calibPts: Pt[] }

/** The sample playground draws on pseudo-sheet 0 — never persisted. */
export const SAMPLE_SHEET_KEY = 0

export type WireRoom = {
  client_id: string
  sheet_id: number
  name: string
  outline: Ring
  holes: Ring[]
  waste: number
  position: number
  product: RoomProduct | null
  subfloor: Subfloor | null
  grout: string | null
  notes: string[]
  multiplier: number
  waste_mode: WasteMode | null
  seams: RoomSeams | null
}

export type WireSurface = {
  client_id: string
  name: string
  position: number
  parent_room_client_id: string | null
  product: RoomProduct | null
  subfloor: Subfloor | null
  grout: string | null
  notes: string[]
  /**
   * The typed worksheet quantity (M8) — presence IS the "manual, not
   * measured" flag; the unit is the product's own. Optional so pre-M8 wire
   * rows stay valid.
   */
  manual_qty?: number | null
}

/** One snake_case shape end-to-end: autosave entry ≡ add_on_row ≡ blob entry. */
export type WireAddOn = AddOnCalc & {
  client_id: string
  position: number
  parent_room_client_id: string | null
  parent_surface_client_id: string | null
  sheet_id: number | null
  item: AddOnItem
  subfloor_condition: SubfloorCondition | null
  product_types?: string[] | null
  print_on_schedule: boolean
}

/** Server-owned rule row — carries the DB pk for PATCH/DELETE. */
export type WireProductAddOn = AddOnCalc & {
  id: number
  product_id: string
  position: number
  item: AddOnItem
  subfloor_condition: SubfloorCondition | null
  print_on_schedule: boolean
}

export type WireCalibration = {
  sheet_id: number
  ft_per_px: number | null
  calib_pts: Pt[] | null
}

export type AutosavePayload = {
  rooms: WireRoom[]
  surfaces: WireSurface[]
  /**
   * Always sent by the M8 client (empty list included). The server treats an
   * ABSENT key as "leave stored add-ons alone" — the stale-tab contract cut
   * plans established — so only a pre-M8 client ever omits it.
   */
  add_ons: WireAddOn[]
  calibrations: WireCalibration[]
  cut_plans: CutPlans
  release: boolean
}

export function documentToPayload(
  rooms: EditorRoom[],
  surfaces: EditorSurface[],
  addOns: EditorAddOn[],
  calibBySheet: Record<number, Calibration>,
  cutPlans: CutPlans,
  release = false
): AutosavePayload {
  // Surfaces may only parent to a room that survives the sample-sheet filter
  // below — otherwise the server rejects the whole payload and the editor
  // silently stops saving. Dropping the parent instead degrades to
  // "unattached", which the schedule renders at the end.
  const persistedRoomIds = new Set(
    rooms.filter((room) => room.sheetId !== SAMPLE_SHEET_KEY).map((room) => room.id)
  )

  return {
    rooms: rooms
      .filter((room) => room.sheetId !== SAMPLE_SHEET_KEY)
      .map((room, index) => ({
        client_id: room.id,
        sheet_id: room.sheetId,
        name: room.name,
        outline: room.outline,
        holes: room.holes,
        waste: room.waste,
        position: index,
        product: room.product,
        subfloor: room.subfloor,
        grout: room.grout,
        notes: room.notes,
        multiplier: room.multiplier,
        waste_mode: room.wasteMode,
        seams: room.seams,
      })),
    surfaces: surfaces.map((surface, index) => ({
      client_id: surface.id,
      name: surface.name,
      position: index,
      parent_room_client_id:
        surface.parentRoomId && persistedRoomIds.has(surface.parentRoomId)
          ? surface.parentRoomId
          : null,
      product: surface.product,
      subfloor: surface.subfloor,
      grout: surface.grout,
      notes: surface.notes,
      manual_qty: surface.manualQty,
    })),
    // An add-on whose parent row is missing from this document DROPS from
    // the payload — its scope must stay exactly-one, so unlike a surface
    // parent it cannot degrade to null, and echoing a dangling reference
    // would 422 the whole payload (a pre-M8 tab can destroy a surface
    // without touching add-ons, orphaning the row server-side).
    add_ons: addOns
      .filter((addOn) => {
        if (addOn.parentRoomId) return persistedRoomIds.has(addOn.parentRoomId)
        if (addOn.parentSurfaceId) return surfaces.some((s) => s.id === addOn.parentSurfaceId)
        return addOn.sheetId !== SAMPLE_SHEET_KEY
      })
      .map((addOn, index) => ({
        client_id: addOn.id,
        position: index,
        parent_room_client_id: addOn.parentRoomId,
        parent_surface_client_id: addOn.parentSurfaceId,
        sheet_id: addOn.sheetId,
        item: addOn.item,
        calc_method: addOn.calc_method,
        coverage_rate: addOn.coverage_rate,
        min_qty: addOn.min_qty,
        count_qty: addOn.count_qty,
        manual_qty: addOn.manual_qty,
        subfloor_condition: addOn.subfloorCondition,
        product_types: addOn.productTypes?.length ? addOn.productTypes : null,
        print_on_schedule: addOn.printOnSchedule,
      })),
    calibrations: Object.entries(calibBySheet)
      .map(([sheetId, calib]) => ({ sheetId: Number(sheetId), calib }))
      .filter(({ sheetId, calib }) => sheetId !== SAMPLE_SHEET_KEY && calib.ftPerPx != null)
      .map(({ sheetId, calib }) => ({
        sheet_id: sheetId,
        ft_per_px: calib.ftPerPx,
        calib_pts: calib.calibPts.length === 2 ? calib.calibPts : null,
      })),
    cut_plans: cutPlans,
    release,
  }
}

// Every spec field defaults tolerantly: rows written before milestone 6 (and
// savepoint blobs replayed from before it) simply arrive spec-less; pre-M7
// rows arrive with no seams, one multiplier, and an inherited waste mode.
export function roomsFromServer(rooms: WireRoom[]): EditorRoom[] {
  return [...rooms]
    .sort((a, b) => a.position - b.position)
    .map((room) => ({
      id: room.client_id,
      sheetId: room.sheet_id,
      name: room.name,
      outline: room.outline,
      holes: room.holes ?? [],
      waste: room.waste ?? 0,
      product: room.product ?? null,
      subfloor: room.subfloor ?? null,
      grout: room.grout ?? null,
      notes: room.notes ?? [],
      multiplier: room.multiplier ?? 1,
      wasteMode: room.waste_mode ?? null,
      seams: room.seams ?? null,
    }))
}

export function surfacesFromServer(surfaces: WireSurface[] | null | undefined): EditorSurface[] {
  return [...(surfaces ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((surface) => ({
      id: surface.client_id,
      name: surface.name,
      parentRoomId: surface.parent_room_client_id ?? null,
      product: surface.product ?? null,
      subfloor: surface.subfloor ?? null,
      grout: surface.grout ?? null,
      notes: surface.notes ?? [],
      manualQty: surface.manual_qty ?? null,
    }))
}

/** Pre-M8 payloads and blobs simply have no add_ons — they arrive add-on-less. */
export function addOnsFromServer(addOns: WireAddOn[] | null | undefined): EditorAddOn[] {
  return [...(addOns ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((addOn) => ({
      id: addOn.client_id,
      parentRoomId: addOn.parent_room_client_id ?? null,
      parentSurfaceId: addOn.parent_surface_client_id ?? null,
      sheetId: addOn.sheet_id ?? null,
      item: addOn.item,
      calc_method: addOn.calc_method,
      coverage_rate: addOn.coverage_rate ?? null,
      min_qty: addOn.min_qty ?? null,
      count_qty: addOn.count_qty ?? null,
      manual_qty: addOn.manual_qty ?? null,
      subfloorCondition: addOn.subfloor_condition ?? null,
      productTypes: addOn.product_types ?? null,
      printOnSchedule: addOn.print_on_schedule !== false,
    }))
}

export function rulesFromServer(rules: WireProductAddOn[] | null | undefined): AddOnRule[] {
  return [...(rules ?? [])]
    .sort((a, b) => a.position - b.position || a.id - b.id)
    .map((rule) => ({
      id: rule.id,
      productId: rule.product_id,
      position: rule.position,
      item: rule.item,
      calc_method: rule.calc_method,
      coverage_rate: rule.coverage_rate ?? null,
      min_qty: rule.min_qty ?? null,
      count_qty: rule.count_qty ?? null,
      manual_qty: rule.manual_qty ?? null,
      subfloorCondition: rule.subfloor_condition ?? null,
      printOnSchedule: rule.print_on_schedule !== false,
    }))
}

export function calibrationsFromSheets(
  sheets: { id: number; ft_per_px: number | null; calib_pts: Pt[] | null }[]
): Record<number, Calibration> {
  const out: Record<number, Calibration> = {}
  for (const sheet of sheets) {
    if (sheet.ft_per_px != null) {
      out[sheet.id] = { ftPerPx: sheet.ft_per_px, calibPts: sheet.calib_pts ?? [] }
    }
  }
  return out
}

/** Continue "Room N" naming past the highest existing N. */
export function nextRoomSeq(rooms: EditorRoom[]): number {
  let max = 0
  for (const room of rooms) {
    const m = /^Room (\d+)$/.exec(room.name)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return Math.max(max, rooms.length)
}

/** Same rule for "Surface N" — a placeholder the estimator renames at once. */
export function nextSurfaceName(surfaces: EditorSurface[]): string {
  let max = 0
  for (const surface of surfaces) {
    const m = /^Surface (\d+)$/.exec(surface.name)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return `Surface ${Math.max(max, surfaces.length) + 1}`
}
